import { useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Pencil,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { QuestionPreview } from "../components/QuestionPreview";
import { useModal } from "../components/ModalProvider";
import { workerUserId } from "../api/client";
import { verifyBank } from "../api/pqx";
import {
  fetchQuestions,
  fetchSubjects,
  deleteQuestion,
  bulkDeleteQuestions,
  type QuestionsPage,
  type QuestionListItem,
} from "../api/worker";

/**
 * Produces a compact page range: [1, "…", 4, 5, 6, "…", 20]
 * Always shows first, last, and up to 2 pages around the current.
 */
function paginationRange(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | "…")[] = [];
  const around = new Set([1, 2, current - 1, current, current + 1, total - 1, total]);

  let prev = 0;
  for (const p of [...around].sort((a, b) => a - b)) {
    if (p < 1 || p > total) continue;
    if (p - prev > 1) pages.push("…");
    pages.push(p);
    prev = p;
  }
  return pages;
}

const TYPE_LABELS: Record<string, string> = {
  mcq: "MCQ",
  essay: "Essay",
  true_false: "True/False",
  multiple_mcq: "Multi-MCQ",
  image_multiple_mcq: "Image MCQ",
};

const STATUS_COLORS: Record<string, string> = {
  active: "bg-positive/15 text-positive",
  draft: "bg-warning/15 text-warning",
  under_review: "bg-accent/15 text-accent-light",
  archived: "bg-text-muted/15 text-text-muted",
  retired: "bg-negative/15 text-negative",
};

export function QuestionsScreen() {
  const [params, setParams] = useSearchParams();
  const { showConfirm, showSuccess, showError } = useModal();
  const [previewQuestion, setPreviewQuestion] = useState<QuestionListItem | null>(null);
  const [previewMode, setPreviewMode] = useState<"preview" | "correct" | "wrong">("preview");
  const [data, setData] = useState<QuestionsPage | null>(null);
  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState<"all" | "mine">("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();

  const myId = workerUserId.get();

  const subjectId = params.get("subjectId") ?? "";
  const search = params.get("search") ?? "";
  const questionType = params.get("type") ?? "";
  const examType = params.get("examType") ?? "";
  const status = params.get("status") ?? "";
  const examYear = params.get("examYear") ?? "";
  const page = params.get("page") ?? "1";

  useEffect(() => {
    fetchSubjects()
      .then((r) => setSubjects(r.subjects))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p: Record<string, string> = { page, limit: "20" };
    if (subjectId) p.subjectId = subjectId;
    if (search) p.search = search;
    if (questionType) p.questionType = questionType;
    if (examType) p.examType = examType;
    if (status) p.status = status;
    if (examYear) p.examYear = examYear;
    if (tab === "mine" && myId) p.createdBy = myId;

    fetchQuestions(p)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [subjectId, search, questionType, examType, status, examYear, page, refreshKey, tab, myId]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setParams(next);
  };

  const goToPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  /**
   * Send the selected questions to the engine to check. It proposes
   * corrections; nothing changes on a question until a reviewer accepts.
   */
  const handleCheck = async () => {
    if (selected.size === 0) return;
    const ok = await showConfirm(
      "Check with the engine",
      `Send ${selected.size} question${selected.size > 1 ? "s" : ""} to the engine to check? It proposes corrections to wrong answers, spelling and tags, and you decide on each. Nothing changes until you accept. They must all be from one subject.`,
    );
    if (!ok) return;
    setChecking(true);
    try {
      const book = await verifyBank([...selected]);
      setSelected(new Set());
      navigate(`/books/${book.book_id}`);
    } catch (e) {
      showError("Couldn't send them", e instanceof Error ? e.message : "Try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    const ok = await showConfirm(
      "Delete Questions",
      `Are you sure you want to delete ${selected.size} question${selected.size > 1 ? "s" : ""}? This cannot be undone.`,
    );
    if (!ok) return;
    try {
      const result = await bulkDeleteQuestions([...selected]);
      setSelected(new Set());
      setRefreshKey((k) => k + 1);
      showSuccess(
        "Deleted",
        `${result.deleted} question${result.deleted !== 1 ? "s" : ""} deleted successfully.`,
      );
    } catch {
      showError("Delete Failed", "Something went wrong while deleting the questions.");
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await showConfirm(
      "Delete Question",
      "Are you sure you want to delete this question? This cannot be undone.",
    );
    if (!ok) return;
    try {
      await deleteQuestion(id);
      setRefreshKey((k) => k + 1);
      showSuccess("Deleted", "Question deleted successfully.");
    } catch {
      showError("Delete Failed", "Something went wrong while deleting the question.");
    }
  };

  const activeFilterCount = [subjectId, questionType, examType, status, examYear, search].filter(
    Boolean,
  ).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[18px] font-bold lg:text-[22px]">Questions</h1>
          <p className="mt-1 text-[13px] text-text-muted">
            {data ? `${data.total.toLocaleString()} questions` : "Loading…"}
          </p>
        </div>
        <Link
          to="/questions/new"
          className="rounded-lg bg-accent px-3 py-2 text-[12px] font-semibold text-bg hover:bg-accent-light lg:px-4 lg:text-[13px]"
        >
          + New Question
        </Link>
      </div>

      {/* Tabs + filter toggle */}
      <div className="mt-4 flex flex-wrap items-center gap-2 lg:mt-5">
        <div className="flex gap-1 rounded-lg border border-border bg-surface p-1">
          <button
            type="button"
            onClick={() => {
              setTab("all");
              setFilter("page", "");
            }}
            className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors lg:px-4 lg:py-2 lg:text-[13px] ${
              tab === "all"
                ? "bg-accent text-bg"
                : "text-text-muted hover:bg-surface-high hover:text-text"
            }`}
          >
            All Workers
          </button>
          <button
            type="button"
            onClick={() => {
              setTab("mine");
              setFilter("page", "");
            }}
            className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition-colors lg:px-4 lg:py-2 lg:text-[13px] ${
              tab === "mine"
                ? "bg-accent text-bg"
                : "text-text-muted hover:bg-surface-high hover:text-text"
            }`}
          >
            My Questions
          </button>
        </div>

        {/* Mobile: filter toggle button */}
        <button
          type="button"
          onClick={() => setFiltersOpen((p) => !p)}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-[12px] font-semibold text-text-muted transition-colors hover:bg-surface-high hover:text-text lg:hidden"
        >
          Filters
          {activeFilterCount > 0 ? (
            <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-bg">
              {activeFilterCount}
            </span>
          ) : null}
          <ChevronDown
            size={14}
            aria-hidden="true"
            className={`ml-0.5 transition-transform ${filtersOpen ? "rotate-180" : ""}`}
          />
        </button>

        {selected.size > 0 ? (
          <button
            type="button"
            disabled={checking}
            onClick={() => void handleCheck()}
            title="The engine checks the answers, spelling and tags. Nothing changes until you accept its corrections."
            className="inline-flex items-center gap-1 rounded-lg bg-accent/15 px-3 py-1.5 text-[12px] font-semibold text-accent-light hover:bg-accent/25 disabled:opacity-50 lg:py-2 lg:text-[13px]"
          >
            <ShieldCheck size={14} aria-hidden="true" /> Check {selected.size} with the engine
          </button>
        ) : null}
        {selected.size > 0 ? (
          <button
            type="button"
            onClick={() => void handleBulkDelete()}
            className="rounded-lg bg-negative/15 px-3 py-1.5 text-[12px] font-semibold text-negative hover:bg-negative/25 lg:py-2 lg:text-[13px]"
          >
            Delete {selected.size}
          </button>
        ) : null}
      </div>

      {/* Filters — always visible on desktop, collapsible on mobile */}
      <div
        className={`mt-3 flex-wrap gap-2 lg:mt-4 lg:flex lg:gap-3 ${filtersOpen ? "flex" : "hidden lg:flex"}`}
      >
        <select
          value={subjectId}
          onChange={(e) => setFilter("subjectId", e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-text outline-none sm:w-auto"
        >
          <option value="">All Subjects</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <select
          value={questionType}
          onChange={(e) => setFilter("type", e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-text outline-none sm:w-auto"
        >
          <option value="">All Types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>

        <select
          value={examType}
          onChange={(e) => setFilter("examType", e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-text outline-none sm:w-auto"
        >
          <option value="">All Exams</option>
          <option value="JAMB">JAMB</option>
          <option value="WAEC">WAEC</option>
          <option value="BOTH">BOTH</option>
        </select>

        <select
          value={status}
          onChange={(e) => setFilter("status", e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-text outline-none sm:w-auto"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="under_review">Under Review</option>
          <option value="archived">Archived</option>
        </select>

        <select
          value={examYear}
          onChange={(e) => setFilter("examYear", e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-text outline-none sm:w-auto"
        >
          <option value="">All Years</option>
          {Array.from({ length: 30 }, (_, i) => new Date().getFullYear() - i).map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search question text…"
          value={search}
          onChange={(e) => setFilter("search", e.target.value)}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-text outline-none placeholder:text-text-muted sm:w-auto"
        />
      </div>

      {/* Questions list */}
      {loading ? (
        <p className="mt-8 text-text-muted">Loading…</p>
      ) : (
        <>
          {/* ── Mobile card view ── */}
          <div className="mt-4 flex flex-col gap-3 lg:hidden">
            {data?.questions.map((q) => (
              <div key={q.id} className="rounded-xl border border-border bg-surface p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selected.has(q.id)}
                    onChange={() => toggleSelect(q.id)}
                    className="mt-1 accent-accent"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] leading-snug">
                      {q.questionText.slice(0, 120)}
                      {q.questionText.length > 120 ? "…" : ""}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="rounded bg-surface-high px-2 py-0.5 text-[10px] font-semibold">
                        {TYPE_LABELS[q.questionType] ?? q.questionType}
                      </span>
                      {q.examType ? (
                        <span className="rounded bg-accent/15 px-2 py-0.5 text-[10px] font-semibold text-accent-light">
                          {q.examType}
                        </span>
                      ) : null}
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[q.status] ?? ""}`}
                      >
                        {q.status}
                      </span>
                    </div>
                    <p className="mt-1.5 text-[11px] text-text-muted">
                      {q.subject?.name ?? "—"}
                      {q.examYear ? ` · ${q.examYear}` : ""}
                      {q.createdBy ? ` · ${q.createdBy.name}` : ""}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex gap-3 border-t border-border pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      setPreviewQuestion(q);
                      setPreviewMode("preview");
                    }}
                    className="text-[12px] text-text-muted hover:text-accent"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Eye size={12} aria-hidden="true" /> Preview
                    </span>
                  </button>
                  <Link
                    to={`/questions/${q.id}`}
                    className="text-[12px] text-accent hover:underline"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Pencil size={12} aria-hidden="true" /> Edit
                    </span>
                  </Link>
                  <button
                    type="button"
                    onClick={() => void handleDelete(q.id)}
                    className="text-[12px] text-negative hover:underline"
                  >
                    <span className="inline-flex items-center gap-1">
                      <Trash2 size={12} aria-hidden="true" /> Delete
                    </span>
                  </button>
                </div>
              </div>
            ))}
            {data?.questions.length === 0 ? (
              <p className="py-8 text-center text-[13px] text-text-muted">No questions found.</p>
            ) : null}
          </div>

          {/* ── Desktop table view ── */}
          <div className="mt-5 hidden overflow-x-auto lg:block">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      checked={
                        data?.questions.length ? selected.size === data.questions.length : false
                      }
                      onChange={() => {
                        if (!data) return;
                        if (selected.size === data.questions.length) setSelected(new Set());
                        else setSelected(new Set(data.questions.map((q) => q.id)));
                      }}
                      className="accent-accent"
                    />
                  </th>
                  <th className="px-3 py-3">Question</th>
                  <th className="px-3 py-3">Type</th>
                  <th className="px-3 py-3">Exam</th>
                  <th className="px-3 py-3">Subject</th>
                  <th className="px-3 py-3">Topic</th>
                  <th className="px-3 py-3">Year</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Added by</th>
                  <th className="px-3 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.questions.map((q) => (
                  <tr key={q.id} className="border-b border-border hover:bg-surface-high/50">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(q.id)}
                        onChange={() => toggleSelect(q.id)}
                        className="accent-accent"
                      />
                    </td>
                    <td className="max-w-[300px] truncate px-3 py-3">
                      {q.questionText.slice(0, 80)}
                      {q.questionText.length > 80 ? "…" : ""}
                    </td>
                    <td className="px-3 py-3">
                      <span className="rounded bg-surface-high px-2 py-0.5 text-[11px] font-semibold">
                        {TYPE_LABELS[q.questionType] ?? q.questionType}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      {q.examType ? (
                        <span className="rounded bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent-light">
                          {q.examType}
                        </span>
                      ) : (
                        <span className="text-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-text-muted">{q.subject?.name ?? "—"}</td>
                    <td className="px-3 py-3 text-text-muted">{q.topic?.name ?? "—"}</td>
                    <td className="px-3 py-3 text-text-muted">{q.examYear ?? "—"}</td>
                    <td className="px-3 py-3">
                      <span
                        className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[q.status] ?? ""}`}
                      >
                        {q.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-text-muted">{q.createdBy?.name ?? "—"}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewQuestion(q);
                          setPreviewMode("preview");
                        }}
                        className="text-text-muted hover:text-accent hover:underline"
                      >
                        Preview
                      </button>
                      <Link to={`/questions/${q.id}`} className="ml-3 text-accent hover:underline">
                        Edit
                      </Link>
                      <button
                        type="button"
                        onClick={() => void handleDelete(q.id)}
                        className="ml-3 text-negative hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 ? (
        <div className="mt-4 flex flex-col items-center gap-3 rounded-lg border border-border bg-surface px-3 py-3 sm:flex-row sm:justify-between sm:px-4 lg:mt-5">
          <p className="text-[12px] text-text-muted">
            Showing{" "}
            <span className="tabular-nums font-semibold text-text">
              {(data.page - 1) * data.limit + 1}–{Math.min(data.page * data.limit, data.total)}
            </span>{" "}
            of{" "}
            <span className="tabular-nums font-semibold text-text">
              {data.total.toLocaleString()}
            </span>
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={data.page <= 1}
              onClick={() => goToPage(data.page - 1)}
              className="rounded-lg px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:bg-surface-high hover:text-text disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <span className="inline-flex items-center gap-1">
                <ChevronLeft size={14} aria-hidden="true" /> Prev
              </span>
            </button>

            {paginationRange(data.page, data.totalPages).map((item, i) =>
              item === "…" ? (
                <span key={`ellipsis-${i}`} className="px-1 text-[13px] text-text-muted">
                  …
                </span>
              ) : (
                <button
                  key={item}
                  type="button"
                  onClick={() => goToPage(item as number)}
                  className={`min-w-[32px] rounded-lg px-2 py-1.5 text-[13px] tabular-nums transition-colors ${
                    item === data.page
                      ? "bg-accent font-semibold text-bg"
                      : "text-text-muted hover:bg-surface-high hover:text-text"
                  }`}
                >
                  {item}
                </button>
              ),
            )}

            <button
              type="button"
              disabled={data.page >= data.totalPages}
              onClick={() => goToPage(data.page + 1)}
              className="rounded-lg px-3 py-1.5 text-[13px] text-text-muted transition-colors hover:bg-surface-high hover:text-text disabled:opacity-30 disabled:hover:bg-transparent"
            >
              <span className="inline-flex items-center gap-1">
                Next <ChevronRight size={14} aria-hidden="true" />
              </span>
            </button>
          </div>
        </div>
      ) : null}

      {/* Question Preview Modal */}
      {previewQuestion ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <button
            type="button"
            aria-label="Close"
            onClick={() => setPreviewQuestion(null)}
            className="absolute inset-0 bg-black/70"
          />
          <div className="relative mx-4 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
            {/* Mode toggle bar */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl border-b border-[#1E2336] bg-[#0A0F1E] px-4 py-3">
              <div className="flex gap-2">
                {(["preview", "correct", "wrong"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setPreviewMode(m)}
                    className={`rounded-lg px-3 py-1.5 text-[12px] font-semibold transition-colors ${
                      previewMode === m
                        ? m === "correct"
                          ? "bg-[#22C55E]/15 text-[#22C55E]"
                          : m === "wrong"
                            ? "bg-[#EF4444]/15 text-[#EF4444]"
                            : "bg-[#6366F1]/15 text-[#818CF8]"
                        : "text-[#8B8FA3] hover:text-[#E8E8E4]"
                    }`}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {m === "preview" ? (
                        <Eye size={13} aria-hidden="true" />
                      ) : m === "correct" ? (
                        <Check size={13} aria-hidden="true" />
                      ) : (
                        <X size={13} aria-hidden="true" />
                      )}
                      {m === "preview" ? "Student view" : m === "correct" ? "Correct" : "Wrong"}
                    </span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPreviewQuestion(null)}
                aria-label="Close preview"
                className="text-text-muted hover:text-text"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>

            <QuestionPreview
              questionText={previewQuestion.questionText}
              questionType={previewQuestion.questionType}
              options={previewQuestion.options}
              correctAnswer={previewQuestion.correctAnswer}
              explanation={previewQuestion.explanation}
              imageUrl={previewQuestion.imageUrl}
              passageContent={previewQuestion.passage?.content ?? null}
              subject={previewQuestion.subject?.name ?? ""}
              topic={previewQuestion.topic?.name ?? ""}
              mode={previewMode}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
